import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api, formatDate, money } from "@/lib/api";
import { toast } from "sonner";
import { useLang } from "@/context/LangContext";
import { useLeviWorkspaceHint } from "@/lib/leviWorkspace";
import SharingCard from "@/components/SharingCard";
import {
  ShieldCheck,
  ArrowLeft,
  ArrowUpRight,
  Loader2,
  Save,
  CalendarCheck,
  FileText,
  Wrench,
  Sparkles,
  Star,
  Handshake,
  Phone,
  ClipboardCheck,
  Receipt,
} from "lucide-react";

const STATUS_STYLES = {
  draft: "bg-[var(--jc-bg)] text-[var(--jc-text-soft)] border border-[var(--jc-border)]",
  ready: "bg-emerald-100 text-emerald-800 border border-emerald-200",
};

const StatusPill = ({ status, label }) => (
  <span
    data-testid={`closeout-status-${status || "draft"}`}
    className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider uppercase ${STATUS_STYLES[status] || STATUS_STYLES.draft}`}
  >
    {label || status || "draft"}
  </span>
);

// ---------------------------------------------------------------------------
// List page
// ---------------------------------------------------------------------------
export const CloseoutPacketsList = () => {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/closeout-packets")
      .then((r) => setItems(r.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <DashboardLayout
      title={t("pages.closeout.title")}
      subtitle={t("pages.closeout.subtitle")}
    >
      {loading ? (
        <div className="jc-card p-8 flex items-center gap-3 text-[var(--jc-text-soft)]">
          <Loader2 className="animate-spin" size={16} /> {t("closeout_form.loading_body")}
        </div>
      ) : items.length === 0 ? (
        <div className="jc-card p-10 text-center border border-dashed border-[var(--jc-border-strong)]" data-testid="closeout-empty-state">
          <ShieldCheck size={32} className="mx-auto text-[var(--jc-text-soft)]" />
          <div className="font-display text-xl mt-3">{t("closeout_form.empty_title")}</div>
          <p className="text-sm text-[var(--jc-text-soft)] mt-2 max-w-md mx-auto">
            {t("closeout_form.empty_body")}
          </p>
          <Link
            to="/app/projects"
            data-testid="closeout-empty-projects-link"
            className="inline-flex items-center gap-1.5 mt-5 px-4 py-2 rounded-md jc-btn-primary text-sm font-semibold"
          >
            {t("work_orders_form.goto_projects")} <ArrowUpRight size={14} />
          </Link>
        </div>
      ) : (
        <div className="jc-card p-0 overflow-hidden" data-testid="closeout-list">
          <table className="w-full text-sm">
            <thead className="bg-[var(--jc-bg)] text-[var(--jc-text-soft)]">
              <tr>
                <th className="text-left py-3 px-4 font-semibold">{t("closeout_form.columns.customer_project")}</th>
                <th className="text-left py-3 px-4 font-semibold hidden md:table-cell">{t("closeout_extra.address")}</th>
                <th className="text-left py-3 px-4 font-semibold">{t("closeout_form.columns.status")}</th>
                <th className="text-left py-3 px-4 font-semibold hidden md:table-cell">{t("closeout_extra.invoice")}</th>
                <th className="text-left py-3 px-4 font-semibold hidden lg:table-cell">{t("closeout_form.columns.generated")}</th>
                <th className="text-left py-3 px-4 font-semibold hidden lg:table-cell">{t("closeout_extra.created")}</th>
                <th className="py-3 px-4"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--jc-border)]">
              {items.map((c) => (
                <tr key={c.id} data-testid={`closeout-row-${c.id}`}>
                  <td className="py-3 px-4">
                    <div className="font-semibold">{c.customer_name || t("closeout_extra.untitled_customer")}</div>
                    <div className="text-xs text-[var(--jc-text-soft)] truncate max-w-[240px]">
                      {c.job_name || t("closeout_extra.project_default")}
                    </div>
                  </td>
                  <td className="py-3 px-4 text-[var(--jc-text-soft)] hidden md:table-cell truncate max-w-[220px]">
                    {c.jobsite_address || "—"}
                  </td>
                  <td className="py-3 px-4"><StatusPill status={c.status} label={t(`closeout_form.status.${c.status || "draft"}`)} /></td>
                  <td className="py-3 px-4 hidden md:table-cell text-[var(--jc-text-soft)]">
                    {c.invoice_status || "—"}
                    {c.invoice_total_customer_facing != null && (
                      <span className="ml-1 text-[var(--jc-text)] font-mono-jc text-xs">· {money(c.invoice_total_customer_facing)}</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-[var(--jc-text-soft)] hidden lg:table-cell">
                    {c.completion_date ? formatDate(c.completion_date) : "—"}
                  </td>
                  <td className="py-3 px-4 text-[var(--jc-text-soft)] hidden lg:table-cell">
                    {formatDate(c.created_at)}
                  </td>
                  <td className="py-3 px-4 text-right">
                    <Link
                      to={`/app/closeout/${c.id}`}
                      data-testid={`closeout-view-${c.id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)]"
                    >
                      {t("work_orders_form.view_packet")} <ArrowUpRight size={12} />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </DashboardLayout>
  );
};

const SectionHeader = ({ icon: Icon, label }) => (
  <div className="flex items-center gap-2 mb-3">
    <Icon size={14} className="text-[var(--jc-text-soft)]" />
    <div className="overline">{label}</div>
  </div>
);

const Field = ({ label, value, onChange, placeholder, rows = 3, testid }) => (
  <div>
    <label className="text-xs font-semibold text-[var(--jc-text-soft)] uppercase tracking-wider mb-1 block">{label}</label>
    <textarea
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      data-testid={testid}
      className="w-full border border-[var(--jc-border)] rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--jc-blue)]/40"
    />
  </div>
);

// ---------------------------------------------------------------------------
// Detail page
// ---------------------------------------------------------------------------
export const CloseoutPacketDetail = () => {
  const { t } = useLang();
  const { id } = useParams();
  const navigate = useNavigate();
  // Phase 5A · Publish current-page workspace hint.
  useLeviWorkspaceHint("closeout_detail", {
    open_document_type: "closeout_packet",
    open_document_id: id,
    current_section: "closeout_detail",
  });
  const [pkt, setPkt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    api
      .get(`/closeout-packets/${id}`)
      .then((r) => {
        setPkt(r.data);
        setForm({
          status: r.data.status || "draft",
          completion_date: r.data.completion_date || "",
          completion_notes: r.data.completion_notes || "",
          final_punch_list_notes: r.data.final_punch_list_notes || "",
          workmanship_warranty_terms: r.data.workmanship_warranty_terms || "",
          manufacturer_warranty_notes: r.data.manufacturer_warranty_notes || "",
          maintenance_notes: r.data.maintenance_notes || "",
          review_request: r.data.review_request || "",
          referral_request: r.data.referral_request || "",
          warranty_contact_notes: r.data.warranty_contact_notes || "",
        });
      })
      .catch(() => {
        toast.error("Could not load closeout packet");
        navigate("/app/closeout");
      });
  }, [id, navigate]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/closeout-packets/${id}`, form);
      setPkt(data);
      toast.success("Closeout packet saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save closeout packet");
    } finally {
      setSaving(false);
    }
  };

  const toggleReady = async () => {
    const next = form.status === "ready" ? "draft" : "ready";
    setForm((f) => ({ ...f, status: next }));
    try {
      const { data } = await api.patch(`/closeout-packets/${id}`, { status: next });
      setPkt(data);
      toast.success(next === "ready" ? "Marked ready for customer" : "Marked as draft");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update status");
    }
  };

  if (!pkt) {
    return (
      <DashboardLayout title={t("pages.closeout.detail_loading_title")} subtitle={t("pages.closeout.detail_loading_subtitle")}>
        <div />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title={pkt.customer_name || "Closeout Packet"}
      subtitle={`${pkt.job_name || "Project"} · ${pkt.jobsite_address || "No address"}`}
      actions={
        <Link
          to="/app/closeout"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--jc-text-soft)] hover:text-[var(--jc-text)]"
          data-testid="closeout-back-link"
        >
          <ArrowLeft size={14} /> {t("closeout_form.back")}
        </Link>
      }
    >
      <div className="mb-4 jc-card p-4 border-l-4 border-l-[var(--jc-teal)] flex items-start gap-3">
        <ShieldCheck size={18} className="text-[var(--jc-teal)] mt-0.5" />
        <div className="text-sm text-[var(--jc-text)]">
          <b>Review and edit this packet before sharing it with your customer.</b> Pricing, cost, markup, margin, supplier, SKU, Double Check, and AI advisory data are intentionally excluded.
        </div>
      </div>

      {/* Slice 2P1 Pass C · Contractor-controlled customer sharing */}
      <div className="mb-6">
        <SharingCard
          kind="closeout"
          docId={id}
          doc={pkt}
          onDocRefresh={() => api.get(`/closeout-packets/${id}`).then((r) => setPkt(r.data))}
        />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Job summary */}
          <section className="jc-card p-6" data-testid="closeout-job-summary">
            <SectionHeader icon={FileText} label={t("closeout_extra.job_summary")} />
            <dl className="grid sm:grid-cols-2 gap-y-3 gap-x-6 text-sm">
              <div><dt className="text-[var(--jc-text-soft)]">Customer</dt><dd className="font-semibold">{pkt.customer_name || "—"}</dd></div>
              <div><dt className="text-[var(--jc-text-soft)]">Project</dt><dd>{pkt.job_name || "—"}</dd></div>
              <div><dt className="text-[var(--jc-text-soft)]">Address</dt><dd>{pkt.jobsite_address || "—"}</dd></div>
              <div><dt className="text-[var(--jc-text-soft)]">Project type</dt><dd>{pkt.project_type || "—"}</dd></div>
              <div>
                <dt className="text-[var(--jc-text-soft)]">Completion date</dt>
                <dd>
                  <input
                    type="date"
                    value={(form.completion_date || "").slice(0, 10)}
                    onChange={(e) => setForm({ ...form, completion_date: e.target.value })}
                    data-testid="closeout-completion-date"
                    className="border border-[var(--jc-border)] rounded-md px-2 py-1 text-sm"
                  />
                </dd>
              </div>
              <div>
                <dt className="text-[var(--jc-text-soft)]">Status</dt>
                <dd>
                  <button
                    onClick={toggleReady}
                    data-testid="closeout-status-toggle"
                    className={`inline-block px-3 py-1 rounded-full text-[11px] font-bold tracking-wider uppercase ${STATUS_STYLES[form.status] || STATUS_STYLES.draft}`}
                  >
                    {form.status || "draft"} · click to toggle
                  </button>
                </dd>
              </div>
            </dl>
            <div className="mt-4 pt-4 border-t border-[var(--jc-border)] grid grid-cols-2 md:grid-cols-4 gap-3 text-xs text-[var(--jc-text-soft)]">
              <div><span className="uppercase tracking-wider">Estimate</span><br /><span className="font-mono-jc text-[var(--jc-text)]">{pkt.linked_estimate_id ? pkt.linked_estimate_id.slice(0, 8) : "—"}</span></div>
              <div><span className="uppercase tracking-wider">Proposal</span><br /><span className="font-mono-jc text-[var(--jc-text)]">{pkt.linked_proposal_id ? pkt.linked_proposal_id.slice(0, 8) : "—"}</span></div>
              <div><span className="uppercase tracking-wider">Contract</span><br /><span className="font-mono-jc text-[var(--jc-text)]">{pkt.linked_contract_id ? pkt.linked_contract_id.slice(0, 8) : "—"}</span></div>
              <div><span className="uppercase tracking-wider">Invoice</span><br /><span className="font-mono-jc text-[var(--jc-text)]">{pkt.linked_invoice_id ? pkt.linked_invoice_id.slice(0, 8) : "—"}</span></div>
            </div>
          </section>

          {/* Work completed */}
          <section className="jc-card p-6" data-testid="closeout-work-completed">
            <SectionHeader icon={ClipboardCheck} label={t("closeout_extra.work_completed")} />
            {pkt.scope_items_safe?.length > 0 ? (
              <table className="w-full text-sm">
                <thead className="text-[var(--jc-text-soft)] text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left py-2">Item</th>
                    <th className="text-right py-2 w-24">Qty</th>
                    <th className="text-left py-2 w-24 pl-3">Unit</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--jc-border)]">
                  {pkt.scope_items_safe.map((s, i) => (
                    <tr key={i}>
                      <td className="py-2 pr-3">
                        <div className="font-semibold">{s.name || "—"}</div>
                        {s.description && <div className="text-xs text-[var(--jc-text-soft)]">{s.description}</div>}
                      </td>
                      <td className="py-2 text-right font-mono-jc">{s.quantity ?? "—"}</td>
                      <td className="py-2 pl-3 text-[var(--jc-text-soft)]">{s.unit || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-sm text-[var(--jc-text-soft)]">No scope items available.</div>
            )}
            {pkt.work_completed_summary && (
              <p className="text-xs text-[var(--jc-text-soft)] mt-3">{pkt.work_completed_summary}</p>
            )}
          </section>

          {/* Final invoice status */}
          <section className="jc-card p-6" data-testid="closeout-invoice-status">
            <SectionHeader icon={Receipt} label={t("closeout_extra.final_invoice_status")} />
            <div className="text-sm">
              <div className="flex items-center justify-between">
                <span className="text-[var(--jc-text-soft)]">Status</span>
                <span className="font-semibold capitalize">{pkt.invoice_status || "—"}</span>
              </div>
              {pkt.invoice_total_customer_facing != null && (
                <div className="flex items-center justify-between mt-2">
                  <span className="text-[var(--jc-text-soft)]">Amount</span>
                  <span className="font-mono-jc">{money(pkt.invoice_total_customer_facing)}</span>
                </div>
              )}
            </div>
          </section>

          <Field
            label={t("closeout_form.sections.notes")}
            value={form.completion_notes}
            onChange={(v) => setForm({ ...form, completion_notes: v })}
            placeholder="Summary of what was completed, jobsite condition at handoff..."
            testid="closeout-completion-notes"
          />

          <Field
            label={t("closeout_form.final_punch")}
            value={form.final_punch_list_notes}
            onChange={(v) => setForm({ ...form, final_punch_list_notes: v })}
            placeholder="Remaining items or notes from the final walkthrough..."
            testid="closeout-punch-notes"
          />
        </div>

        <aside className="space-y-4">
          <section className="jc-card p-6" data-testid="closeout-workmanship">
            <SectionHeader icon={ShieldCheck} label={t("closeout_form.sections.warranty_overview")} />
            <Field
              label={t("closeout_form.field_terms")}
              value={form.workmanship_warranty_terms}
              onChange={(v) => setForm({ ...form, workmanship_warranty_terms: v })}
              placeholder="Duration, scope, exclusions..."
              rows={4}
              testid="closeout-workmanship-input"
            />
          </section>

          <section className="jc-card p-6" data-testid="closeout-manufacturer">
            <SectionHeader icon={Wrench} label={t("closeout_form.manufacturer_warranty")} />
            <Field
              label={t("closeout_form.sections.notes")}
              value={form.manufacturer_warranty_notes}
              onChange={(v) => setForm({ ...form, manufacturer_warranty_notes: v })}
              placeholder="Product/material warranties, registration info..."
              rows={4}
              testid="closeout-manufacturer-input"
            />
          </section>

          <section className="jc-card p-6" data-testid="closeout-maintenance">
            <SectionHeader icon={Sparkles} label={t("closeout_form.sections.maintenance")} />
            <Field
              label={t("closeout_form.field_care")}
              value={form.maintenance_notes}
              onChange={(v) => setForm({ ...form, maintenance_notes: v })}
              placeholder="Care instructions, owner responsibilities..."
              rows={4}
              testid="closeout-maintenance-input"
            />
          </section>

          <section className="jc-card p-6" data-testid="closeout-review">
            <SectionHeader icon={Star} label={t("closeout_form.sections.next_steps")} />
            <Field
              label={t("closeout_form.field_message")}
              value={form.review_request}
              onChange={(v) => setForm({ ...form, review_request: v })}
              placeholder="Thank the customer and ask for a review..."
              rows={3}
              testid="closeout-review-input"
            />
          </section>

          <section className="jc-card p-6" data-testid="closeout-referral">
            <SectionHeader icon={Handshake} label={t("closeout_form.referral_request")} />
            <Field
              label={t("closeout_form.field_message")}
              value={form.referral_request}
              onChange={(v) => setForm({ ...form, referral_request: v })}
              placeholder="Ask for referrals..."
              rows={3}
              testid="closeout-referral-input"
            />
          </section>

          <section className="jc-card p-6" data-testid="closeout-contact">
            <SectionHeader icon={Phone} label={t("closeout_form.service_contact")} />
            <Field
              label={t("closeout_form.field_contact")}
              value={form.warranty_contact_notes}
              onChange={(v) => setForm({ ...form, warranty_contact_notes: v })}
              placeholder="Who to contact, phone/email, response expectations..."
              rows={3}
              testid="closeout-contact-input"
            />
          </section>

          <button
            onClick={save}
            disabled={saving}
            data-testid="closeout-save-btn"
            className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-md jc-btn-blue text-sm font-semibold disabled:opacity-60"
          >
            {saving ? <><Loader2 className="animate-spin" size={14} /> Saving...</> : <><Save size={14} /> Save changes</>}
          </button>

          <div className="jc-card p-4 text-xs text-[var(--jc-text-soft)] flex gap-2">
            <CalendarCheck size={14} className="shrink-0 mt-0.5" />
            <div>Last saved {formatDate(pkt.updated_at)}. Use the sharing controls above to give the customer access to this packet.</div>
          </div>
        </aside>
      </div>
    </DashboardLayout>
  );
};

export default CloseoutPacketsList;
